import {
    Alert,
    Button,
    CheckBox,
    Form,
    InputControl,
    InputLabel,
    Loading,
    NumberInput,
    Picker,
    RequiredText,
    Spacer,
    StyleFunction,
    Surface,
    Text,
    TextInput,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import { GQDiscountValueType, GQProductFormQuery } from "graphql-sdk";
import { IProductInput, parseAmount, SupportedCurrenciesType } from "lib";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";
import { Divider } from "react-native-paper";
import Select from "react-select";

import produce from "immer";
import {
    selectComponents,
    selectStyles,
} from "../../../components/multi-picker";
import { ParentPickerSection } from "./parent-picker-section";
import { BarcodeSection } from "./barcode-section";
import { DiscountTemplateSection } from "./discount-template-section";

export function ProductForm(props: {
    form: Form<IProductInput>;
    printers: GQProductFormQuery["printers"]["data"];
    virtualPrinters: GQProductFormQuery["virtualPrinters"]["data"];
    vats: GQProductFormQuery["vats"]["data"];
    productGroups: GQProductFormQuery["productGroups"]["data"];
    products: GQProductFormQuery["products"]["data"];
    onSubmit: () => void;
    currency: SupportedCurrenciesType;
    submitButton: [string, string];
    productId?: string;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const theme = useTheme();

    const [{ values, errors }, { setValue, handleSubmit }] = props.form;
    const valuesRef = useRef(values);

    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const onAddBarcode = useCallback(() => {
        const next = addBarcodeRow(valuesRef.current?.barcodes);
        setValue("barcodes", next);
    }, [setValue]);

    const onBarcodeChange = useCallback(
        (text, index: number) => {
            if (text === undefined || text.trim() === "") {
                return;
            }
            const next = updateBarcodeRow(
                valuesRef.current?.barcodes,
                index,
                text
            );
            setValue("barcodes", next);
        },
        [setValue]
    );

    const onRemoveBarcode = useCallback(
        (_item, index) => {
            const next = removeBarcodeRow(valuesRef.current?.barcodes, index);
            setValue("barcodes", next);
        },
        [setValue]
    );

    const onAddDiscountTemplate = useCallback(() => {
        const next = addDiscountTemplateRow(
            valuesRef.current?.discountTemplate
        );
        setValue("discountTemplate", next);
    }, [setValue]);

    const onDiscountTemplateChange = useCallback(
        (
            value: any, // TODO: Better type.
            type: "name" | "type" | "value" | "color",
            index: number
        ) => {
            if (value === undefined) {
                return;
            }

            const next = updateDiscountTemplateRow(
                valuesRef.current?.discountTemplate,
                index,
                item => {
                    switch (type) {
                        case "name":
                            item.name = value.trim();
                            break;
                        case "type":
                            item.type = value as GQDiscountValueType;
                            break;
                        case "value":
                            item.value = value;
                            break;
                        case "color":
                            item.color = value;
                            break;
                    }
                }
            );
            setValue("discountTemplate", next);
        },
        [setValue]
    );

    const onRemoveDiscountTemplate = useCallback(
        (_item, index) => {
            const next = removeDiscountTemplateRow(
                valuesRef.current?.discountTemplate,
                index
            );
            setValue("discountTemplate", next);
        },
        [setValue]
    );

    const printerList = useMemo(() => {
        return (
            props.printers
                .filter(itr => itr.class === "Generic")
                .filter(itr => itr.type !== "INTERNAL")
                .map(printer => ({
                    value: printer.id,
                    label: printer.name,
                })) || []
        );
    }, [props.printers]);

    const virtualPrinterList = useMemo(() => {
        return (
            props.virtualPrinters.map(itr => ({
                label: itr.name,
                value: itr.id,
            })) || []
        );
    }, [props.virtualPrinters]);

    const printerOptions = useMemo(
        () => [
            {
                label: t(
                    "backoffice.product_form.printer.do_not_print",
                    "Do not print"
                ),
                value: "",
            },
            {
                label: t(
                    "backoffice.product_form.printer.printers",
                    "Printers"
                ),
                options: printerList,
            },
            {
                label: t(
                    "backoffice.product_form.printer.virtual_printers",
                    "Virtual printers"
                ),
                options: virtualPrinterList,
            },
        ],
        [printerList, t, virtualPrinterList]
    );

    const productGroupItems = useMemo(
        () => (
            <>
                <Picker.Item
                    label={t(
                        "backoffice.product_form.choose_product_group",
                        "Choose a product group"
                    )}
                    value=""
                    testID="noProductGroup"
                />
                {props.productGroups.map(productGroup => (
                    <Picker.Item
                        key={productGroup.id}
                        value={productGroup.id}
                        label={productGroup.name}
                        testID={"group:" + productGroup.name}
                    />
                ))}
            </>
        ),
        [props.productGroups, t]
    );

    const defaultPrinterValue = useMemo(() => {
        if (!valuesRef.current?.printerId) {
            return [];
        }

        let doNotPrint = printerOptions[0];
        const printers = printerOptions[1]?.options?.find(
            itr => itr.value === valuesRef.current.printerId
        );
        const virtualPrinters = printerOptions[2]?.options?.find(
            itr => itr.value === valuesRef.current.printerId
        );

        return printers ?? virtualPrinters ?? doNotPrint;
    }, [printerOptions]);

    const parentOptions = useMemo(
        () =>
            props.products
                .filter(
                    product =>
                        !props.productId || product.id !== props.productId
                )
                .map(product => ({
                    value: product.id,
                    label: product.name,
                })) || [],
        [props.products, props.productId]
    );

    const onParentChange = useCallback(
        (value: string) => {
            setValue("parentId", value !== "" ? value : null);
        },
        [setValue]
    );

    if (!values) {
        return (
            <View style={styles.container}>
                <Surface>
                    <Loading />
                </Surface>
            </View>
        );
    }

    return (
        <View style={styles.container} testID="product">
            {values.isExternal && (
                <Alert type="info" style={styles.readonlyContainer}>
                    {t(
                        "backoffice.common.readonly_product",
                        "This product is controlled by an external system via the API and most of the properties can only be changed from there."
                    )}
                </Alert>
            )}

            <Surface>
                <InputControl
                    error={errors.name}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <TextInput
                        label={t("common.name", "Name")}
                        placeholder={t(
                            "backoffice.common.enter_name",
                            "Enter name"
                        )}
                        value={values.name}
                        onChangeText={text => setValue("name", text)}
                        disabled={values.isExternal}
                        testID="name"
                        required={true}
                    />
                </InputControl>

                <InputControl
                    error={errors.active}
                    description={t(
                        "backoffice.product_form.active_description",
                        "Notice: If a product is set to inactive and the product is used in a layout, the product will be removed from the layout"
                    )}
                >
                    <CheckBox
                        label={t("common.active", "Active")}
                        value={values.active}
                        onValueChange={checked => setValue("active", checked)}
                        testID="active"
                    />
                </InputControl>

                <InputControl
                    error={errors.groupId}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <Picker
                        label={t("common.product_group", "Product group")}
                        onValueChange={value => {
                            setValue("groupId", value);
                        }}
                        selectedValue={values.groupId}
                        disabled={values.isExternal}
                        testID="group"
                        required={true}
                    >
                        {productGroupItems}
                    </Picker>
                </InputControl>
                <InputControl
                    error={errors.externalId}
                    description={
                        values.isExternal
                            ? `${t(
                                  "backoffice.product_form.external_id_description",
                                  "Number or ID that is used by another system. Used for reference."
                              )} ${t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )}`
                            : t(
                                  "backoffice.product_form.external_id_description",
                                  "Number or ID that is used by another system. Used for reference."
                              )
                    }
                >
                    <TextInput
                        label={t(
                            "backoffice.product_form.external_id",
                            "Number"
                        )}
                        placeholder={t(
                            "backoffice.product_form.enter_external_id",
                            "Enter number"
                        )}
                        value={values.externalId || ""}
                        onChangeText={text =>
                            setValue("externalId", text || null)
                        }
                        disabled={values.isExternal}
                        testID="externalId"
                    />
                </InputControl>

                <BarcodeSection
                    value={values.barcodes}
                    error={errors.barcodes}
                    styles={styles}
                    t={t}
                    productId={props.productId}
                    isExternal={values.isExternal}
                    onAddBarcode={onAddBarcode}
                    onBarcodeChange={onBarcodeChange}
                    onRemoveBarcode={onRemoveBarcode}
                />

                <ParentPickerSection
                    value={values.parentId}
                    error={errors.parentId}
                    t={t}
                    onParentChange={onParentChange}
                    parentOptions={parentOptions}
                    isExternal={values.isExternal}
                />

                <InputControl
                    error={errors.amount}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <NumberInput
                        label={t(
                            "backoffice.product_form.price_incl_vat",
                            "Price incl VAT"
                        )}
                        placeholder={t(
                            "backoffice.product_form.enter_price_incl_vat",
                            "Enter price incl VAT"
                        )}
                        value={values.amount / 100}
                        onChange={value => {
                            setValue("amount", parseAmount(value || 0));
                        }}
                        decimals={2}
                        unit={props.currency}
                        disabled={values.isExternal}
                        testID="amount"
                    />
                    {values.amount < 0 && (
                        <View>
                            <Text style={styles.info}>
                                {t(
                                    "backoffice.product_form.amount_is_negative",
                                    "Notice: The amount is negative"
                                )}
                            </Text>
                        </View>
                    )}
                </InputControl>

                <InputControl
                    error={errors.minimumAmount}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <NumberInput
                        label={t(
                            "backoffice.product_form.minimum_price",
                            "Minimum price"
                        )}
                        placeholder={t(
                            "backoffice.product_form.enter_minimum_price",
                            "Enter minimum price"
                        )}
                        value={values.minimumAmount / 100}
                        onChange={value =>
                            setValue("minimumAmount", parseAmount(value || 0))
                        }
                        disabled={values.amount < 0 || values.isExternal}
                        decimals={2}
                        unit={props.currency}
                        testID="minimumAmount"
                    />
                </InputControl>
                <InputControl
                    error={errors.costAmount}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <NumberInput
                        label={t(
                            "backoffice.product_form.cost_price",
                            "Cost price"
                        )}
                        placeholder={t(
                            "backoffice.product_form.enter_cost_price",
                            "Enter cost price"
                        )}
                        value={values.costAmount / 100}
                        onChange={value =>
                            setValue("costAmount", parseAmount(value || 0))
                        }
                        decimals={2}
                        unit={props.currency}
                        disabled={values.isExternal}
                        testID="costPrice"
                    />
                </InputControl>

                <InputControl
                    error={errors.ownVatId}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <Picker
                        label={t("backoffice.product_form.vat", "VAT")}
                        onValueChange={text =>
                            setValue("ownVatId", text === "" ? null : text)
                        }
                        selectedValue={values.ownVatId || undefined}
                        disabled={values.isExternal}
                        testID="vat"
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.product_form.use_group__vat",
                                "Use product group VAT"
                            )}
                            value=""
                        />
                        {props.vats.map(vat => {
                            return (
                                <Picker.Item
                                    key={vat.id}
                                    value={vat.id}
                                    label={vat.name}
                                    testID={"vat:" + vat.name}
                                />
                            );
                        })}
                    </Picker>
                </InputControl>
                <InputControl
                    error={errors.ownExcludeFromDiscounts}
                    description={
                        values.isExternal
                            ? t(
                                  "backoffice.common.readonly_value",
                                  "This value is controlled by an external system via the API and can only be changed from here."
                              )
                            : null
                    }
                >
                    <CheckBox
                        label={t(
                            "backoffice.product_form.exclude_from_discounts",
                            "Exclude from discounts"
                        )}
                        value={values.ownExcludeFromDiscounts}
                        onValueChange={value =>
                            setValue("ownExcludeFromDiscounts", value)
                        }
                        disabled={values.isExternal}
                        testID="ownExludeFromDiscounts"
                    />
                </InputControl>

                <Divider />
                <Spacer space={2} />

                <DiscountTemplateSection
                    values={values}
                    errors={errors}
                    styles={styles}
                    theme={theme}
                    t={t}
                    currency={props.currency}
                    onAddDiscountTemplate={onAddDiscountTemplate}
                    onDiscountTemplateChange={onDiscountTemplateChange}
                    onRemoveDiscountTemplate={onRemoveDiscountTemplate}
                />

                {values.isExternal ? <Divider style={styles.divider} /> : null}

                <InputControl
                    error={errors.buttonText}
                    description={t(
                        "backoffice.product_form.plu_description",
                        "If set, the product can be found by this code on the cash register"
                    )}
                >
                    <NumberInput
                        label={t("backoffice.product_form.plu_label", "PLU")}
                        value={values.plu || null}
                        onChange={value => setValue("plu", value || null)}
                        testID="plu"
                    />
                </InputControl>

                <InputControl
                    error={errors.buttonText}
                    description={t(
                        "backoffice.product_form.button_text_description",
                        "If set, the button text will be shown on the cash register instead of the product name"
                    )}
                >
                    <TextInput
                        label={t(
                            "backoffice.product_form.button_text_label",
                            "Button Text"
                        )}
                        value={values.buttonText || ""}
                        onChangeText={text =>
                            setValue("buttonText", text !== "" ? text : null)
                        }
                        testID="buttonText"
                    />
                </InputControl>

                <InputControl
                    error={errors.printerId || undefined}
                    description={t(
                        "backoffice.product_form.printer.override_product_group_settings",
                        "Uses setting from product group unless overridden here."
                    )}
                >
                    <InputLabel>
                        {t(
                            "backoffice.product_form.printer.do_print",
                            "Send to printer for order handling"
                        )}
                    </InputLabel>

                    <View style={styles.row}>
                        <View style={styles.checkbox}>
                            <CheckBox
                                value={!values.inheritPrinter}
                                onValueChange={newValue =>
                                    setValue("inheritPrinter", !newValue)
                                }
                                testID="printerSelect"
                            />
                            <View style={styles.input}>
                                <Select
                                    // This must be set to avoid having the dropdown hidden by other inputs
                                    menuPortalTarget={document.body}
                                    isSearchable={false}
                                    defaultValue={defaultPrinterValue}
                                    options={printerOptions}
                                    onChange={itm => {
                                        setValue(
                                            "printerId",
                                            (itm as any)?.value || ""
                                        );
                                    }}
                                    styles={selectStyles}
                                    components={selectComponents}
                                    isDisabled={values.inheritPrinter}
                                />
                            </View>
                        </View>
                    </View>
                </InputControl>
                <Button onPress={handleSubmit(props.onSubmit)} testID="save">
                    {t(props.submitButton[0], props.submitButton[1])}
                </Button>
                <RequiredText />
            </Surface>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        width: theme.dimensions.maxFormWidth,
    },
    row: {
        flexDirection: "row",
    },
    checkbox: {
        ...theme.styles.flex1,
        ...theme.styles.row,
        gap: theme.spacingScale,
    },
    divider: {
        marginBottom: theme.spacingScale * 2,
    },
    input: {
        flex: 1,
    },
    info: {
        fontSize: 12,
        color: theme.colors.warning,
    },
    readonlyContainer: {
        marginBottom: theme.spacingScale * 2,
    },
    readonlyText: {
        ...theme.fonts.thin,
        fontSize: 14,
        color: theme.colors.textDark,
        fontStyle: "italic",
    },
    addBarcode: {
        color: theme.colors.textDark,
    },
    addDiscountTemplate: {
        color: theme.colors.textDark,
    },

    removableRow: {
        backgroundColor: theme.colors.grey50,
        marginBottom: 10,
        borderTopEndRadius: theme.borderRadiusTiny,
        borderBottomEndRadius: theme.borderRadiusTiny,
        zIndex: 1,
    },
    discountRow: {
        flexDirection: "column",
        flex: 1,
        backgroundColor: theme.colors.white,
        borderColor: theme.colors.grey500,
        borderWidth: 1,
        padding: theme.spacingScale,
    },
    textInput: { flex: 1 },
    colorPickerElevation: {
        zIndex: 2,
    },
});

function addBarcodeRow(barcodes?: IProductInput["barcodes"]) {
    return (
        produce(barcodes, draft => {
            draft?.push({
                id: undefined,
                barcode: "",
            });
        }) || [{ barcode: "" }]
    );
}

function updateBarcodeRow(
    barcodes: IProductInput["barcodes"] | undefined,
    index: number,
    text: string
) {
    return (
        produce(barcodes, draft => {
            if (draft !== undefined) {
                draft[index].barcode = text;
            }
        }) || [{ barcode: "" }]
    );
}

function removeBarcodeRow(
    barcodes: IProductInput["barcodes"] | undefined,
    index: number
) {
    const next = produce(barcodes, draft => {
        draft?.splice(index, 1);
    });

    return next.length > 0 ? next : [{ barcode: "" }];
}

function addDiscountTemplateRow(
    discountTemplate: IProductInput["discountTemplate"] | undefined
) {
    return (
        produce(discountTemplate, draft => {
            draft?.push({
                name: undefined,
                type: undefined,
                value: 0,
            });
        }) || []
    );
}

function updateDiscountTemplateRow(
    discountTemplate: IProductInput["discountTemplate"] | undefined,
    index: number,
    updater: (
        item: NonNullable<IProductInput["discountTemplate"]>[number]
    ) => void
) {
    return (
        produce(discountTemplate, draft => {
            if (draft !== undefined) {
                updater(draft[index]);
            }
        }) || []
    );
}

function removeDiscountTemplateRow(
    discountTemplate: IProductInput["discountTemplate"] | undefined,
    index: number
) {
    return (
        produce(discountTemplate, draft => {
            draft?.splice(index, 1);
        }) || []
    );
}
